const BASE = ''; // same-origin (Pi serves both /api and /v2)

export class ApiError extends Error {
  constructor(public status: number, public path: string, msg: string) {
    super(`${path}: ${status} ${msg}`);
    this.name = 'ApiError';
  }
}

// In-flight dedupe (perf 2026-08-11). App.tsx en ControlsColumn vroegen bij
// mount dezelfde endpoints op; op de tech-route deed TechPage hetzelfde naast
// App.tsx. Twee gelijktijdige GET's op dezelfde URL delen nu één request.
//
// Bewust GEEN cache met houdbaarheid: er wordt niets bewaard nadat het
// antwoord binnen is. Een tweede aanroep ná afronding doet dus gewoon een
// nieuwe request en de polling-versheid verandert niet — alleen exact
// samenvallende requests worden samengevoegd.
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Voer `run` uit onder `key`, of haak aan op de al lopende uitvoering met
 * dezelfde key. Ook bruikbaar voor fetchers die hun eigen fetch-aanroep doen
 * (zie api/reserveLock.ts).
 */
export function dedupe<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const started = run().finally(() => {
    if (inFlight.get(key) === started) inFlight.delete(key);
  });
  inFlight.set(key, started);
  return started;
}

export async function getJson<T>(path: string): Promise<T> {
  return dedupe(`GET ${path}`, () => getJsonUncached<T>(path));
}

async function getJsonUncached<T>(path: string): Promise<T> {
  // credentials: 'include' — backend mag de zbs_session cookie zien zodat
  // cookie-auth-protected reads (zoals /api/config/effective sinds H9) werken.
  // Read-endpoints zonder auth-check zijn niet getroffen — same-origin cookie
  // is harmless als 'm genegeerd wordt.
  const res = await fetch(BASE + path, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    throw new ApiError(res.status, path, body.slice(0, 200));
  }
  return res.json() as Promise<T>;
}

export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let txt = '';
    try { txt = await res.text(); } catch { /* ignore */ }
    throw new ApiError(res.status, path, txt.slice(0, 200));
  }
  // 204 / lege bodies → returneer null cast naar T
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}
